import {
  Experiment,
  ExperimentClient,
  Variants,
  Variant,
} from '@amplitude/experiment-js-client';
import { datadogRum } from '@datadog/browser-rum';
import get from 'lodash/get';

import {
  getIsInitialised as getIsAmplitudeInitialized,
  getAmplitudeDeviceId,
  setAmplitudeDeviceId,
  getAmplitudeDeviceInfo,
} from '../analytics/vendors/amplitude';
import ErrorHandler from '@wetransfer/transfer/modules/error-handler';

import { setCachedVariant, getVariantCache } from './cache/variantCache';
import { WT_EXPERIMENTS_INITIALIZED } from './constants';

let experiment: null | ExperimentClient;
let errors = false;
let isInitialized = false;

export const getIsInitialised = (): boolean => {
  return isInitialized;
};

const markAsInitialized = (): void => {
  isInitialized = true;

  window.dispatchEvent(new Event(WT_EXPERIMENTS_INITIALIZED));
};

const setDeviceId = (): void => {
  if (window.__amplitude__.deviceId) return;

  const deviceId = getAmplitudeDeviceId() || getAmplitudeUser()?.key;

  if (!deviceId) return;

  setAmplitudeDeviceId(deviceId);
  window.__amplitude__.deviceId = deviceId;
};

const setUserAgentInfo = (): void => {
  if (window.__amplitude__.userAgent) return;

  const userAgentInfo = getAmplitudeDeviceInfo();

  if (!userAgentInfo) return;

  window.__amplitude__.userAgent = userAgentInfo;
};

export const initExperiments = async ({
  deploymentId,
  onInitComplete,
}: {
  deploymentId: string;
  onInitComplete: () => void;
}): Promise<void> => {
  if (!deploymentId || !getIsAmplitudeInitialized()) {
    return;
  }

  try {
    experiment = Experiment.initializeWithAmplitudeAnalytics(deploymentId, {
      fetchTimeoutMillis: 5000,
    });

    setDeviceId();
    setUserAgentInfo();

    // fetch() can be called without params because we initialize amplitude analytics first and that caches the deviceId/userId for both SDKs: Analytics and Experiments.
    // Takes around 400ms from the AMS office wifi, probably slower for most of our users
    await experiment.fetch();

    // Instant operation after getting all of the experiments above.
    const allFlags = await experiment.all();

    window.__amplitude__.feature_flags = allFlags;
    emptyVariantsObjectWillThrow();

    datadogRum.addRumGlobalContext('experiments', allFlags);
  } catch (error) {
    ErrorHandler.track(error);
    errors = true;
  }

  markAsInitialized();
  onInitComplete();
};

export const clearExperiments = (): void => {
  experiment = undefined;
  delete window.__amplitude__;
  isInitialized = false;
};

/**
 * Variant type includes an optional payload property
 * @param {string} key Variant unique identifier
 * @param {Variant} offlineFallback Returned value when amplitude is not reachable
 * @returns {Variant}
 */
export const getFullVariant = (
  key: string,
  offlineFallback: Variant = {}
): Variant => {
  // Look for the variant fetched at app initialization
  let variant = getInitialVariant(key);

  if (variant?.value === undefined) {
    // If no initial value is found, enter fallback logic
    variant = getFallbackVariant(key, offlineFallback);
  }

  if (variant === undefined) {
    return {};
  }

  // setCachedVariant does not store undefined or empty string values
  setCachedVariant(key, variant);

  if (experiment) {
    // Manually track an exposure event
    // We need to do this because exposure events are not sent when
    // we get all the flag variants at once via experiment.all()
    // This operation is instant, it just queues an exposure event
    experiment.exposure(key);
  }

  return variant || {};
};

/**
 * Excludes the optional payload property, only interested in Variant.value (string)
 * @deprecated
 * Use getFullVariant instead
 */
export const getVariant = (
  key: string,
  offlineValue?: string
): string | undefined => {
  const variant = getFullVariant(key, { value: offlineValue });

  return variant?.value || undefined;
};

/**
 * ONLY when there is an error with Amplitude, return fallback in order of precedence:
 * 1. Cached value from local storage
 * 2. Default variant argument passed to getVariant or getFullVariant
 *
 * @param {string} key Flag name
 * @param {Variant} offlineVariant Fallback to return when Amplitude is offline or does not initialize correctly
 * @returns {Variant}
 */

function getFallbackVariant(
  key: string,
  offlineVariant: Variant = {}
): Variant {
  // Errors are set during initialization
  if (errors) {
    const cachedFlags = getVariantCache();

    if (
      cachedFlags !== null &&
      cachedFlags !== undefined &&
      cachedFlags[key] !== undefined
    ) {
      return cachedFlags[key];
    }

    return offlineVariant;
  }

  return {};
}

export function getInitialVariant(key: string): Variant {
  if (getAllFlags() !== undefined) {
    return getAllFlags()[key];
  }

  return {};
}

export function emptyVariantsObjectWillThrow(): void {
  if (
    window.__amplitude__.feature_flags &&
    Object.keys(window.__amplitude__.feature_flags).length === 0
  ) {
    throw new Error('Empty flag object returned from experiment server');
  }
}

export function getAmplitudeUser(): AmplitudeUser {
  return get(window, '__amplitude__.user');
}

export function getAllFlags(): Variants {
  return get(window, '__amplitude__.feature_flags');
}

export default {
  initExperiments,
  getVariant,
  getAllFlags,
  getFullVariant,
};
